import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  XMarkIcon, 
  CalculatorIcon,
  InformationCircleIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline'
import { useAuth } from '@/hooks/useAuth'
import { loanService, CreateLoanApplicationData } from '@/services/loanService'
import api from '@/services/api'
import toast from 'react-hot-toast'

// Interface pour les employés (gardée car utilisée dans le composant)
interface Employee {
  id: number
  firstName: string
  lastName: string
  employeeNumber: string
  department: string
  position: string
  baseSalary?: number
}

// Interface pour les types de prêt
interface LoanType {
  id: number
  name: string
  interestRate: number
  minAmount: number
  maxAmount: number | null
  maxTermMonths: number | null
  processingFee: number
  requiresGuarantor: boolean
  requiresCollateral: boolean
}

interface LoanApplicationFormData {
  loanTypeId: string
  requestedAmount: string
  loanTermMonths: string
  purpose: string
  guarantorEmployeeId?: string
  collateralDescription?: string
}

interface LoanApplicationModalProps {
  onClose: () => void
  onSuccess: () => void
}

const LoanApplicationModal: React.FC<LoanApplicationModalProps> = ({ onClose, onSuccess }) => {
  const { user } = useAuth()
  const [formData, setFormData] = useState<LoanApplicationFormData>({
    loanTypeId: '',
    requestedAmount: '',
    loanTermMonths: '',
    purpose: '',
    guarantorEmployeeId: '',
    collateralDescription: ''
  })
  const [monthlyPayment, setMonthlyPayment] = useState<number>(0)
  const [totalInterest, setTotalInterest] = useState<number>(0)

  const queryClient = useQueryClient()

  // Get active loan types
  const { data: loanTypesData, isLoading: loanTypesLoading } = useQuery({
    queryKey: ['activeLoanTypes'],
    queryFn: () => loanService.getLoanTypes(true),
    staleTime: 5 * 60 * 1000,
  })

  // Get employees for guarantor selection
  const { data: employeesData, isLoading: employeesLoading } = useQuery({
    queryKey: ['employeesForGuarantor'],
    queryFn: async () => {
      const response = await api.get('/employees', { params: { limit: 100, employmentStatus: 'active' } })
      return response.data
    },
    staleTime: 5 * 60 * 1000,
  })

  // Create loan application mutation
  const createApplicationMutation = useMutation({
    mutationFn: async (data: CreateLoanApplicationData) => {
      return loanService.createLoanApplication(data)
    },
    onSuccess: () => {
      toast.success('Loan application submitted successfully')
      queryClient.invalidateQueries({ queryKey: ['loanApplications'] })
      queryClient.invalidateQueries({ queryKey: ['loans'] })
      onSuccess()
      onClose()
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Failed to submit loan application')
    }
  })

  const loanTypes: LoanType[] = loanTypesData?.data?.loanTypes || []
  const employees: Employee[] = employeesData?.data?.employees || []

  // Get selected loan type details avec typage correct
  const selectedLoanType = loanTypes.find((lt: LoanType) => lt.id === parseInt(formData.loanTypeId))

  // Calculate loan details when values change
  React.useEffect(() => {
    if (selectedLoanType && formData.requestedAmount && formData.loanTermMonths) {
      const principal = parseFloat(formData.requestedAmount)
      const term = parseInt(formData.loanTermMonths)
      
      if (principal > 0 && term > 0) {
        const monthly = loanService.calculateMonthlyPayment(principal, selectedLoanType.interestRate, term)
        const interest = loanService.calculateTotalInterest(principal, selectedLoanType.interestRate, term)
        
        setMonthlyPayment(monthly)
        setTotalInterest(interest)
      } else {
        setMonthlyPayment(0)
        setTotalInterest(0)
      }
    }
  }, [selectedLoanType, formData.requestedAmount, formData.loanTermMonths])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.loanTypeId) {
      toast.error('Please select a loan type')
      return
    }

    if (!formData.requestedAmount || parseFloat(formData.requestedAmount) <= 0) {
      toast.error('Please enter a valid loan amount')
      return
    }

    if (!formData.loanTermMonths || parseInt(formData.loanTermMonths) <= 0) {
      toast.error('Please enter a valid loan term')
      return
    }

    if (!formData.purpose.trim()) {
      toast.error('Please provide a purpose for the loan')
      return
    }

    // Validate against loan type constraints
    if (selectedLoanType) {
      const amount = parseFloat(formData.requestedAmount)
      const term = parseInt(formData.loanTermMonths)

      if (amount < selectedLoanType.minAmount) {
        toast.error(`Minimum loan amount is ${loanService.formatCurrency(selectedLoanType.minAmount)}`)
        return
      }

      if (selectedLoanType.maxAmount && amount > selectedLoanType.maxAmount) {
        toast.error(`Maximum loan amount is ${loanService.formatCurrency(selectedLoanType.maxAmount)}`)
        return
      }

      if (selectedLoanType.maxTermMonths && term > selectedLoanType.maxTermMonths) {
        toast.error(`Maximum loan term is ${selectedLoanType.maxTermMonths} months`)
        return
      }

      if (selectedLoanType.requiresGuarantor && !formData.guarantorEmployeeId) {
        toast.error('This loan type requires a guarantor')
        return
      }

      if (selectedLoanType.requiresCollateral && !formData.collateralDescription?.trim()) {
        toast.error('This loan type requires collateral description')
        return
      }
    }

    const applicationData: CreateLoanApplicationData = {
      loanTypeId: parseInt(formData.loanTypeId),
      requestedAmount: parseFloat(formData.requestedAmount),
      loanTermMonths: parseInt(formData.loanTermMonths),
      purpose: formData.purpose.trim(),
    }

    if (formData.guarantorEmployeeId) {
      applicationData.guarantorEmployeeId = parseInt(formData.guarantorEmployeeId)
    }

    if (formData.collateralDescription?.trim()) {
      applicationData.collateralDescription = formData.collateralDescription.trim()
    }

    createApplicationMutation.mutate(applicationData)
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount)
  }

  // Styles communs pour les champs
  const inputStyles = "w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-500 dark:focus:border-blue-500 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-all duration-200"
  
  const selectStyles = "w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-500 dark:focus:border-blue-500 text-gray-900 dark:text-white appearance-none cursor-pointer transition-all duration-200"
  
  const textareaStyles = "w-full px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-500 dark:focus:border-blue-500 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 resize-none transition-all duration-200"

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
      <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            Apply for Loan
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors duration-200 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Loan Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Loan Type <span className="text-red-500">*</span>
            </label>
            {loanTypesLoading ? (
              <div className="animate-pulse bg-gray-200 dark:bg-gray-700 h-12 rounded-xl"></div>
            ) : (
              <div className="relative">
                <select
                  value={formData.loanTypeId}
                  onChange={(e) => setFormData({ ...formData, loanTypeId: e.target.value })}
                  className={selectStyles}
                  required
                >
                  <option value="" className="text-gray-500 dark:text-gray-400">Select loan type...</option>
                  {loanTypes.map((loanType: LoanType) => (
                    <option key={loanType.id} value={loanType.id} className="text-gray-900 dark:text-white">
                      {loanType.name} - {loanType.interestRate}% interest
                    </option>
                  ))}
                </select>
                <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
              </div>
            )}
          </div>

          {/* Loan Type Details */}
          {selectedLoanType && (
            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
              <div className="flex items-start">
                <InformationCircleIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-3 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <div className="font-medium text-blue-900 dark:text-blue-100 mb-2">Loan Type Details:</div>
                  <div className="text-blue-800 dark:text-blue-200 space-y-1.5">
                    <div>• Interest Rate: <span className="font-semibold">{selectedLoanType.interestRate}%</span></div>
                    <div>• Amount Range: <span className="font-semibold">{formatCurrency(selectedLoanType.minAmount)} - {selectedLoanType.maxAmount ? formatCurrency(selectedLoanType.maxAmount) : 'No limit'}</span></div>
                    {selectedLoanType.maxTermMonths && (
                      <div>• Max Term: <span className="font-semibold">{selectedLoanType.maxTermMonths} months</span></div>
                    )}
                    <div>• Processing Fee: <span className="font-semibold">{formatCurrency(selectedLoanType.processingFee)}</span></div>
                    {selectedLoanType.requiresGuarantor && (
                      <div className="text-orange-700 dark:text-orange-300 font-medium">⚠️ Requires a guarantor</div>
                    )}
                    {selectedLoanType.requiresCollateral && (
                      <div className="text-orange-700 dark:text-orange-300 font-medium">⚠️ Requires collateral</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Amount and Term */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Requested Amount (USD) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={formData.requestedAmount}
                onChange={(e) => setFormData({ ...formData, requestedAmount: e.target.value })}
                className={inputStyles}
                placeholder="0.00"
                required
              />
              {selectedLoanType && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Min: {formatCurrency(selectedLoanType.minAmount)}
                  {selectedLoanType.maxAmount && ` • Max: ${formatCurrency(selectedLoanType.maxAmount)}`}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Loan Term (Months) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="1"
                max={selectedLoanType?.maxTermMonths || 240}
                value={formData.loanTermMonths}
                onChange={(e) => setFormData({ ...formData, loanTermMonths: e.target.value })}
                className={inputStyles}
                placeholder="12"
                required
              />
              {selectedLoanType?.maxTermMonths && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  Max: {selectedLoanType.maxTermMonths} months
                </p>
              )}
            </div>
          </div>

          {/* Loan Calculations */}
          {monthlyPayment > 0 && (
            <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl">
              <div className="flex items-center">
                <CalculatorIcon className="h-5 w-5 text-green-600 dark:text-green-400 mr-3 flex-shrink-0" />
                <div className="text-sm">
                  <div className="font-medium text-green-900 dark:text-green-100 mb-2">Loan Calculations:</div>
                  <div className="text-green-800 dark:text-green-200 space-y-1.5">
                    <div>• Monthly Payment: <span className="font-semibold">{formatCurrency(monthlyPayment)}</span></div>
                    <div>• Total Interest: <span className="font-semibold">{formatCurrency(totalInterest)}</span></div>
                    <div>• Total Repayment: <span className="font-semibold">{formatCurrency(parseFloat(formData.requestedAmount) + totalInterest)}</span></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Purpose */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Purpose of Loan <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.purpose}
              onChange={(e) => setFormData({ ...formData, purpose: e.target.value })}
              rows={3}
              className={textareaStyles}
              placeholder="Describe the purpose of this loan..."
              required
            />
          </div>

          {/* Guarantor Selection */}
          {selectedLoanType?.requiresGuarantor && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Guarantor <span className="text-red-500">*</span>
              </label>
              {employeesLoading ? (
                <div className="animate-pulse bg-gray-200 dark:bg-gray-700 h-12 rounded-xl"></div>
              ) : (
                <div className="relative">
                  <select
                    value={formData.guarantorEmployeeId}
                    onChange={(e) => setFormData({ ...formData, guarantorEmployeeId: e.target.value })}
                    className={selectStyles}
                    required
                  >
                    <option value="" className="text-gray-500 dark:text-gray-400">Select guarantor...</option>
                    {employees
                      .filter((emp: Employee) => emp.id !== user?.employee?.id) // Exclude current user
                      .map((employee: Employee) => (
                        <option key={employee.id} value={employee.id} className="text-gray-900 dark:text-white">
                          {employee.firstName} {employee.lastName} ({employee.employeeNumber}) - {employee.department}
                        </option>
                      ))}
                  </select>
                  <ChevronDownIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
                </div>
              )}
            </div>
          )}

          {/* Collateral Description */}
          {selectedLoanType?.requiresCollateral && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Collateral Description <span className="text-red-500">*</span>
              </label>
              <textarea
                value={formData.collateralDescription}
                onChange={(e) => setFormData({ ...formData, collateralDescription: e.target.value })}
                rows={3}
                className={textareaStyles}
                placeholder="Describe the collateral being offered..."
                required
              />
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createApplicationMutation.isPending || monthlyPayment <= 0}
              className="px-6 py-3 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-xl hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
            >
              {createApplicationMutation.isPending ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Submitting...</span>
                </div>
              ) : (
                'Submit Application'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default LoanApplicationModal